import {
  BRAND,
  COLLECTIONS,
  formatPhoneForDisplay,
  LIMITS,
  REGISTRANT_TYPE_LABELS,
  threadIdFor,
  validateRegistration,
  whatsappShareUrl,
  type JobDoc,
  type JobReportRow,
  type RegistrantType,
  type RegistrationDoc,
} from '@crew/shared';
import { collection, doc, getDocFromServer, getDocsFromServer } from 'firebase/firestore';
import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAdmin } from '@/auth/AuthProvider';
import { demoStore } from '@/demo/demoStore';
import { IS_DEMO } from '@/demo/demoMode';
import { useContractors, useJobPendingRequests } from '@/hooks/useAdminData';
import { useJobRegistrations, type JobView, type RegistrationView } from '@/hooks/useActiveJobs';
import { isThreadUnread, useJobThreads } from '@/hooks/useMessaging';
import { copyText } from '@/lib/clipboard';
import { printJobReport } from '@/lib/report';
import { adminApi, readerUid } from '@/services/adminApi';
import { ApiClientError, errorMessage } from '@/services/apiError';
import { getFirebase } from '@/services/firebase';
import { personalUrl } from '@/services/publicApi';
import { ChatIcon, CopyIcon, EditIcon, PhoneIcon, PlusIcon, TrashIcon, WhatsAppIcon } from '../icons';
import { EditPersonForm } from '../contractors/EditPersonForm';
import { RequestCard } from '../requests/RequestCard';
import { Button } from '../ui/Button';
import { Dialog } from '../ui/Dialog';
import { Notice } from '../ui/Notice';
import { NumberStepper } from '../ui/NumberStepper';
import { Spinner } from '../ui/Spinner';
import { TextField } from '../ui/TextField';
import { useToast } from '../ui/Toast';

/** הנרשמים לעבודה: רשימה, בקשות ממתינות, הוספה ידנית, עריכה, קישור אישי ו-PDF. */
export function RegistrationsPanel({ job }: { job: JobView }) {
  const regs = useJobRegistrations(job.id);
  const pending = useJobPendingRequests(job.id);
  const threads = useJobThreads(job.id);
  const { user } = useAdmin();
  const uid = readerUid(user);
  const unreadChats = useMemo(
    () => new Set(threads.state === 'ready' ? threads.data.filter((t) => isThreadUnread(t, uid)).map((t) => t.contractorId) : []),
    [threads, uid],
  );
  const [selected, setSelected] = useState<RegistrationView | null>(null);
  const [adding, setAdding] = useState(false);
  const [issued, setIssued] = useState<{ token: string; name: string; phone: string } | null>(null);
  const [printing, setPrinting] = useState(false);
  const toast = useToast();
  const active = job.status === 'active';

  const sorted = useMemo(() => {
    if (regs.state !== 'ready') return [];
    return [...regs.data].sort(
      (a, b) => (a.registrantType === 'contractor' ? 0 : 1) - (b.registrantType === 'contractor' ? 0 : 1) || a.fullName.localeCompare(b.fullName, 'he'),
    );
  }, [regs]);

  async function downloadPdf() {
    setPrinting(true);
    try {
      // הנתונים העדכניים ביותר — ישירות מהשרת, לא מהמטמון
      let rows: JobReportRow[];
      let fresh: Pick<JobDoc, 'name' | 'date' | 'reportTime' | 'location'> = job;
      if (IS_DEMO) {
        rows = demoStore.registrations(job.id).map(toRow);
      } else {
        const { db } = getFirebase();
        const [jobSnap, regSnap] = await Promise.all([
          getDocFromServer(doc(db, COLLECTIONS.jobs, job.id)),
          getDocsFromServer(collection(db, COLLECTIONS.jobs, job.id, COLLECTIONS.registrations)),
        ]);
        if (jobSnap.exists()) fresh = jobSnap.data() as JobDoc;
        rows = regSnap.docs.map((d) => toRow(d.data() as RegistrationDoc));
      }
      await printJobReport(fresh, rows);
    } catch (err) {
      toast(err instanceof ApiClientError ? err.message : 'הפקת הדוח נכשלה. בדקו את החיבור ונסו שוב', 'error');
    } finally {
      setPrinting(false);
    }
  }

  return (
    <section className="rounded-card border border-line bg-surface p-4 md:p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-[17px] font-bold">
          נרשמים {regs.state === 'ready' && <span className="font-medium text-ink-3">({regs.data.length})</span>}
          <span className="ms-2 inline-flex items-center rounded-[6px] bg-tag px-2 py-0.5 align-middle text-[13px] font-bold text-tag-ink shadow-[inset_0_-2px_0_var(--color-tag-bar)]">
            <span className="tabular">{job.totalWorkers}</span>&nbsp;עובדים
          </span>
        </h2>
        <div className="flex gap-2">
          <Button variant="secondary" className="min-h-10 px-3 text-[14px]" loading={printing} onClick={downloadPdf}>
            PDF
          </Button>
          {active && (
            <Button className="min-h-10 px-3 text-[14px]" icon={<PlusIcon size={17} />} onClick={() => setAdding(true)}>
              הוספה
            </Button>
          )}
        </div>
      </div>

      {pending.state === 'ready' && pending.data.length > 0 && (
        <div className="mb-4 space-y-3">
          <h3 className="text-[14px] font-semibold text-danger">ממתינות לאישור ({pending.data.length})</h3>
          {pending.data.map((r) => (
            <RequestCard key={r.id} r={r} showJob={false} />
          ))}
        </div>
      )}

      {regs.state === 'loading' && (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      )}
      {regs.state === 'error' && <Notice tone="error">{regs.message}</Notice>}
      {regs.state === 'ready' && sorted.length === 0 && (
        <p className="rounded-control border border-dashed border-line-strong p-5 text-center text-[14px] text-ink-2">
          אין עדיין נרשמים. שלחו את קישור ההרשמה והקוד — קבלנים ועובדים יופיעו כאן ברגע שיירשמו.
        </p>
      )}
      {sorted.length > 0 && (
        <ul className="-mx-2 divide-y divide-line">
          {sorted.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => setSelected(r)}
                className="flex w-full items-center gap-3 rounded-[8px] px-2 py-2.5 text-start hover:bg-sunken/60 active:bg-sunken"
              >
                <TypeChip type={r.registrantType} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[15px] font-semibold">{r.fullName}</div>
                  <div className="ltr text-start text-[13px] text-ink-2">{formatPhoneForDisplay(r.phone)}</div>
                </div>
                {unreadChats.has(r.id) && (
                  <span className="relative shrink-0 text-brand" title="הודעה חדשה בשיחה">
                    <ChatIcon size={18} />
                    <span className="absolute -end-0.5 -top-0.5 size-2 rounded-full bg-danger" />
                  </span>
                )}
                {r.pendingRequestId && <span className="size-2.5 shrink-0 rounded-full bg-warn" title="יש בקשה ממתינה" />}
                {r.source === 'admin' && <span className="shrink-0 text-[11px] text-ink-3">ידני</span>}
                <span className="tabular min-w-10 rounded-[6px] bg-tag px-2 py-1 text-center text-[15px] font-bold text-tag-ink shadow-[inset_0_-3px_0_var(--color-tag-bar)]">
                  {r.workerCount}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <RegistrationDialog
        job={job}
        reg={selected && regs.state === 'ready' ? (regs.data.find((r) => r.id === selected.id) ?? selected) : selected}
        onClose={() => setSelected(null)}
        onIssued={(token, r) => {
          setSelected(null);
          setIssued({ token, name: r.fullName, phone: r.phone });
        }}
      />
      <AddRegistrationDialog
        open={adding}
        job={job}
        onClose={() => setAdding(false)}
        onAdded={(token, name, phone) => {
          setAdding(false);
          setIssued({ token, name, phone });
        }}
      />
      <PersonalLinkDialog issued={issued} jobName={job.name} onClose={() => setIssued(null)} />
    </section>
  );
}

function toRow(r: Pick<RegistrationDoc, 'registrantType' | 'fullName' | 'phone' | 'workerCount'>): JobReportRow {
  return { registrantType: r.registrantType, name: r.fullName, phone: r.phone, workerCount: r.workerCount };
}

export function TypeChip({ type }: { type: RegistrantType }) {
  return (
    <span className={`shrink-0 rounded-[5px] px-1.5 py-0.5 text-[11px] font-semibold ${type === 'contractor' ? 'bg-brand text-white' : 'bg-sunken text-ink-2'}`}>
      {REGISTRANT_TYPE_LABELS[type]}
    </span>
  );
}

/** wa.me עם מספר הנמען (בלי +) */
function whatsappTo(phoneE164: string, text: string): string {
  return `https://wa.me/${phoneE164.replace(/^\+/, '')}?text=${encodeURIComponent(text)}`;
}

function RegistrationDialog({
  job,
  reg,
  onClose,
  onIssued,
}: {
  job: JobView;
  reg: RegistrationView | null;
  onClose: () => void;
  onIssued: (token: string, reg: RegistrationView) => void;
}) {
  const { user } = useAdmin();
  const toast = useToast();
  const [count, setCount] = useState<number | ''>('');
  const [busy, setBusy] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<'remove' | 'revoke' | null>(null);
  const [lastId, setLastId] = useState<string | null>(null);
  const [editingDetails, setEditingDetails] = useState(false);
  if (reg && reg.id !== lastId) {
    setLastId(reg.id);
    setCount(reg.workerCount);
    setConfirm(null);
    setEditingDetails(false);
  }
  if (!reg) return null;
  const api = adminApi(user);
  const active = job.status === 'active';

  async function run(key: string, fn: () => Promise<unknown>, done: string, close = true) {
    setBusy(key);
    try {
      await fn();
      toast(done);
      if (close) onClose();
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(null);
    }
  }

  return (
    <Dialog open onClose={onClose} title={reg.fullName}>
      <div className="flex items-center gap-2">
        <TypeChip type={reg.registrantType} />
        <a href={`tel:${reg.phone}`} className="ltr inline-flex items-center gap-1 text-[15px] font-medium text-brand">
          <PhoneIcon size={15} />
          {formatPhoneForDisplay(reg.phone)}
        </a>
        {reg.source === 'admin' && <span className="text-[12px] text-ink-3">נוסף ידנית</span>}
        {!editingDetails && (
          <button
            type="button"
            onClick={() => setEditingDetails(true)}
            className="ms-auto inline-flex min-h-9 items-center gap-1 rounded-control px-2 text-[13px] font-semibold text-brand hover:bg-sunken"
          >
            <EditIcon size={15} />
            עריכת שם וטלפון
          </button>
        )}
      </div>
      {editingDetails && (
        <div className="mt-3 rounded-control border border-line bg-paper p-3">
          <EditPersonForm
            person={{ id: reg.id, fullName: reg.fullName, phone: reg.phone }}
            onDone={() => setEditingDetails(false)}
            onCancel={() => setEditingDetails(false)}
          />
        </div>
      )}
      <Link
        to={`/messages/${threadIdFor(job.id, reg.id)}`}
        className="mt-3 flex min-h-11 items-center justify-center gap-2 rounded-control border border-line-strong text-[15px] font-semibold text-brand hover:bg-sunken"
      >
        <ChatIcon size={18} />
        שיחה עם {reg.fullName.split(' ')[0]}
      </Link>
      {reg.pendingRequestId && (
        <div className="mt-3">
          <Notice tone="info">יש בקשה ממתינה. שינוי ידני של הכמות יסגור אותה.</Notice>
        </div>
      )}

      {reg.registrantType === 'contractor' && active && (
        <div className="mt-5">
          <NumberStepper label="כמות עובדים" value={count} onChange={setCount} min={1} max={LIMITS.maxWorkers} />
          <Button
            block
            className="mt-3"
            disabled={count === '' || count === reg.workerCount}
            loading={busy === 'count'}
            onClick={() => run('count', () => api.updateCount(job.id, reg.id, count as number), 'הכמות עודכנה')}
          >
            שמירת הכמות
          </Button>
        </div>
      )}

      {IS_DEMO && reg.personalTokenHash && (
        <div className="mt-4 rounded-control border border-dashed border-line-strong p-3 text-[13px]">
          <a href={`/c/${reg.personalTokenHash}`} target="_blank" rel="noopener noreferrer" className="font-semibold text-brand underline">
            לראות מה {reg.fullName.split(' ')[0]} רואה בדף האישי שלו
          </a>
          <p className="mt-1 text-ink-3">קיים רק במצב תצוגה, כדי לבדוק את מסך הקבלן. במערכת האמיתית הקישור נשלח לנרשם ורק הוא פותח אותו.</p>
        </div>
      )}

      {active && (
        <div className="mt-6 border-t border-line pt-4">
          <h3 className="text-[15px] font-semibold text-ink">קישור אישי</h3>
          <p className="mt-1 text-[13px]">
            {reg.personalTokenHash ? 'לנרשם יש קישור פעיל. קישור חדש מבטל את הקודם.' : 'הקישור האישי בוטל. אפשר להנפיק חדש.'}
          </p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <Button
              variant="secondary"
              loading={busy === 'reissue'}
              onClick={async () => {
                setBusy('reissue');
                try {
                  const res = await api.tokenAction(job.id, reg.id, 'reissue');
                  if (res.personalToken) onIssued(res.personalToken, reg);
                } catch (err) {
                  toast(errorMessage(err), 'error');
                } finally {
                  setBusy(null);
                }
              }}
            >
              הנפקת קישור חדש
            </Button>
            {reg.personalTokenHash && (
              <Button variant="ghost" onClick={() => setConfirm('revoke')}>
                ביטול הקישור
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="mt-6 border-t border-line pt-4">
        {confirm === 'remove' ? (
          <div className="rounded-control bg-danger-soft p-3">
            <p className="text-[14px] font-semibold text-danger">
              להסיר את {reg.fullName} מהעבודה? {reg.workerCount} עובדים יירדו מהסיכום והקישור האישי יבוטל.
            </p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Button variant="secondary" onClick={() => setConfirm(null)}>
                ביטול
              </Button>
              <Button variant="danger" loading={busy === 'remove'} onClick={() => run('remove', () => api.removeRegistration(job.id, reg.id), 'הרישום הוסר')}>
                הסרה
              </Button>
            </div>
          </div>
        ) : confirm === 'revoke' ? (
          <div className="rounded-control bg-warn-soft p-3">
            <p className="text-[14px] font-semibold">לבטל את הקישור האישי? הנרשם לא יוכל לשלוח בקשות עד שתנפיקו קישור חדש.</p>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Button variant="secondary" onClick={() => setConfirm(null)}>
                חזרה
              </Button>
              <Button loading={busy === 'revoke'} onClick={() => run('revoke', () => api.tokenAction(job.id, reg.id, 'revoke'), 'הקישור בוטל')}>
                ביטול הקישור
              </Button>
            </div>
          </div>
        ) : (
          <Button variant="ghost" className="text-danger hover:bg-danger-soft" icon={<TrashIcon size={17} />} onClick={() => setConfirm('remove')}>
            הסרה מהעבודה
          </Button>
        )}
      </div>
    </Dialog>
  );
}

function AddRegistrationDialog({
  open,
  job,
  onClose,
  onAdded,
}: {
  open: boolean;
  job: JobView;
  onClose: () => void;
  onAdded: (token: string, name: string, phone: string) => void;
}) {
  const { user } = useAdmin();
  const [mode, setMode] = useState<'pool' | 'new'>('pool');
  const [search, setSearch] = useState('');
  const [pick, setPick] = useState<{ id: string; fullName: string; phone: string } | null>(null);
  const [type, setType] = useState<RegistrantType>('contractor');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [count, setCount] = useState<number | ''>('');
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const pool = useContractors();
  const regs = useJobRegistrations(job.id);
  const registered = new Set(regs.state === 'ready' ? regs.data.map((r) => r.id) : []);

  const term = search.trim().replace(/-/g, '');
  const matches =
    pool.state === 'ready'
      ? pool.data
          .filter((c) => !registered.has(c.id))
          .filter((c) => !term || c.fullName.includes(search.trim()) || `0${c.phone.slice(4)}`.includes(term))
          .slice(0, 30)
      : [];

  function reset() {
    setPick(null);
    setSearch('');
    setName('');
    setPhone('');
    setCount('');
    setErrors({});
    setError(null);
    setType('contractor');
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const body =
      mode === 'pool' && pick
        ? { contractorId: pick.id, registrantType: type, ...(type === 'contractor' && count !== '' ? { workerCount: count } : {}) }
        : { registrantType: type, fullName: name, phone, ...(type === 'contractor' && count !== '' ? { workerCount: count } : {}) };
    if (mode === 'new') {
      const v = validateRegistration({ registrantType: type, fullName: name, phone, ...(count === '' ? {} : { workerCount: count }) });
      if (!v.ok) return setErrors(v.errors);
    } else if (!pick) return setError('יש לבחור מהרשימה');
    else if (type === 'contractor' && (count === '' || count < 1)) return setErrors({ workerCount: 'יש להזין כמות עובדים' });
    setBusy(true);
    try {
      const res = await adminApi(user).addRegistration(job.id, body);
      const who = mode === 'pool' && pick ? pick : { fullName: name, phone: phone };
      onAdded(res.personalToken, who.fullName, who.phone.startsWith('+') ? who.phone : `+972${who.phone.replace(/\D/g, '').replace(/^0/, '')}`);
      reset();
    } catch (err) {
      if (err instanceof ApiClientError && err.fields) setErrors(err.fields);
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="הוספת נרשם ידנית"
    >
      <form onSubmit={submit} noValidate className="space-y-4">
        <div role="tablist" className="grid grid-cols-2 rounded-control bg-sunken p-1">
          {(['pool', 'new'] as const).map((m) => (
            <button
              key={m}
              type="button"
              role="tab"
              aria-selected={mode === m}
              onClick={() => {
                setMode(m);
                setError(null);
              }}
              className={`min-h-10 rounded-[8px] text-[14px] font-medium ${mode === m ? 'bg-surface text-ink shadow-sm' : 'text-ink-2'}`}
            >
              {m === 'pool' ? 'מהמאגר' : 'חדש'}
            </button>
          ))}
        </div>
        {error && <Notice tone="error">{error}</Notice>}

        {mode === 'pool' ? (
          pick ? (
            <div className="flex items-center justify-between rounded-control border border-brand bg-brand-soft p-3">
              <div>
                <div className="font-semibold text-ink">{pick.fullName}</div>
                <div className="ltr text-start text-[13px]">{formatPhoneForDisplay(pick.phone)}</div>
              </div>
              <button type="button" className="text-[14px] font-medium text-brand underline" onClick={() => setPick(null)}>
                החלפה
              </button>
            </div>
          ) : (
            <div>
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="חיפוש לפי שם או טלפון"
                aria-label="חיפוש במאגר"
                className="block min-h-12 w-full rounded-control border border-line-strong px-3.5 text-[16px] focus:border-brand focus:outline-none"
              />
              <ul className="mt-2 max-h-56 divide-y divide-line overflow-y-auto rounded-control border border-line">
                {pool.state === 'loading' && <li className="p-3 text-center text-[14px]">טוען…</li>}
                {pool.state === 'ready' && matches.length === 0 && <li className="p-3 text-center text-[14px]">לא נמצאו התאמות</li>}
                {matches.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 px-3 py-2.5 text-start hover:bg-sunken"
                      onClick={() => {
                        setPick({ id: c.id, fullName: c.fullName, phone: c.phone });
                        setType(c.lastRegistrantType ?? 'contractor');
                      }}
                    >
                      <TypeChip type={c.lastRegistrantType ?? 'contractor'} />
                      <span className="flex-1 truncate font-medium text-ink">{c.fullName}</span>
                      <span className="ltr text-[13px]">{formatPhoneForDisplay(c.phone)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )
        ) : (
          <>
            <TextField label="שם מלא" value={name} onChange={(e) => setName(e.target.value)} error={errors.fullName} />
            <TextField label="טלפון" type="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} error={errors.phone} placeholder="050-1234567" />
          </>
        )}

        {(mode === 'new' || pick) && (
          <>
            <div className="grid grid-cols-2 gap-2">
              {(['contractor', 'worker'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  aria-pressed={type === t}
                  onClick={() => setType(t)}
                  className={`min-h-11 rounded-control border-2 text-[15px] font-semibold ${type === t ? 'border-brand bg-brand-soft text-ink' : 'border-line-strong text-ink-2'}`}
                >
                  {t === 'contractor' ? 'קבלן' : 'עובד עצמאי'}
                </button>
              ))}
            </div>
            {type === 'contractor' && <NumberStepper label="כמות עובדים" value={count} onChange={setCount} min={1} max={LIMITS.maxWorkers} error={errors.workerCount ?? null} />}
            <Button type="submit" block loading={busy}>
              הוספה לעבודה
            </Button>
          </>
        )}
      </form>
    </Dialog>
  );
}

/** אחרי הוספה או הנפקה — הקישור האישי לשליחה לנרשם. */
function PersonalLinkDialog({ issued, jobName, onClose }: { issued: { token: string; name: string; phone: string } | null; jobName: string; onClose: () => void }) {
  const toast = useToast();
  if (!issued) return null;
  const link = personalUrl(issued.token);
  const message = [`${BRAND.name}`, `שלום ${issued.name},`, `הקישור האישי שלך לעבודה "${jobName}":`, link, '', 'דרכו אפשר לבקש שינוי בכמות או ביטול הגעה.'].join('\n');
  return (
    <Dialog
      open
      onClose={onClose}
      title="קישור אישי מוכן"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            סגירה
          </Button>
          <Button variant="secondary" icon={<CopyIcon size={18} />} onClick={async () => toast((await copyText(link)) ? 'הקישור הועתק' : 'ההעתקה נכשלה')}>
            העתקה
          </Button>
          <a
            href={whatsappTo(issued.phone, message)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex min-h-12 items-center justify-center gap-2 rounded-control bg-[#1f7a4d] px-5 text-[15px] font-semibold text-white"
          >
            <WhatsAppIcon size={18} />
            שליחה ל{issued.name.split(' ')[0]}
          </a>
        </>
      }
    >
      <p>שלחו ל{issued.name} את הקישור האישי. מטעמי אבטחה הוא מוצג רק עכשיו — אחר כך אפשר רק להנפיק חדש.</p>
      <p className="ltr mt-3 break-all rounded-control bg-sunken p-3 text-start text-[13px] text-ink">{link}</p>
    </Dialog>
  );
}
